/*
 * Strings for the asset hierarchy feature (features/cmms) — page, tree, details
 * panel, dialogs and the asset document components.
 *
 * Follows the repo convention of a messages.js next to the feature with a local
 * `scope` const (see features/machineDetails/components/messages.js).
 * Cross-feature strings — common verbs and the enum label helpers — stay in
 * cmmsMessages.js so cmmsWorkOrders and cmmsRecurringSchedules can share them.
 */
import { defineMessages } from "react-intl";

export const scope = "axionoee.features.cmms.assethierarchy";

export default defineMessages({
  // page
  pageTitle: { id: `${scope}.page.title`, defaultMessage: "Asset hierarchy" },
  pageSubtitle: {
    id: `${scope}.page.subtitle`,
    defaultMessage: "Manage CMMS structure, maintenance objects, and optional OEE machine links in one place.",
  },

  // page tabs: the same hierarchy as a tree or as a table
  tabTree: { id: `${scope}.tab.tree`, defaultMessage: "Tree" },
  tabTable: { id: `${scope}.tab.table`, defaultMessage: "Table" },

  // toolbar
  addNode: { id: `${scope}.toolbar.addNode`, defaultMessage: "Add node" },
  includeInactive: { id: `${scope}.toolbar.includeInactive`, defaultMessage: "Include inactive" },
  includeInactiveHint: {
    id: `${scope}.toolbar.includeInactiveHint`,
    defaultMessage: "Show archived nodes in the hierarchy view.",
  },

  // tree
  noAssetNodes: { id: `${scope}.tree.empty`, defaultMessage: "No asset nodes" },
  emptyTreeHint: {
    id: `${scope}.tree.emptyHint`,
    defaultMessage: "No asset hierarchy has been set up for this company yet.",
  },
  createRootNode: { id: `${scope}.tree.createRoot`, defaultMessage: "Create site" },

  // table view. Name, type, parent, description and sort order reuse the field.*
  // messages below, and the OEE machine column reuses machineLink.field: the table's headers and
  // the details panel's labels name the same things and must not drift apart.
  columnId: { id: `${scope}.table.column.id`, defaultMessage: "Id" },
  columnPath: { id: `${scope}.table.column.path`, defaultMessage: "Path" },
  columnStatus: { id: `${scope}.table.column.status`, defaultMessage: "Status" },
  // Names the fields, as the other four CMMS search boxes do — the ancestry is searched too, and
  // nobody would guess that from a box labelled "Search".
  filterSearch: {
    id: `${scope}.table.filter.search`,
    defaultMessage: "Search by name, description, type, path or machine",
  },
  filterClear: { id: `${scope}.table.filter.clear`, defaultMessage: "Clear filters" },
  tableNoMatchTitle: { id: `${scope}.table.noMatchTitle`, defaultMessage: "No matching nodes" },
  tableNoMatchHint: {
    id: `${scope}.table.noMatchHint`,
    defaultMessage: "No asset node matches your search.",
  },

  // stat tiles — upper-cased by CSS, not by the message
  statRootNodes: { id: `${scope}.stat.rootNodes`, defaultMessage: "Root nodes" },
  statTotalNodes: { id: `${scope}.stat.totalNodes`, defaultMessage: "Total nodes" },

  // details panel
  selectNodeTitle: { id: `${scope}.details.selectNodeTitle`, defaultMessage: "Select a node" },
  selectNodeHint: {
    id: `${scope}.details.selectNodeHint`,
    defaultMessage: "Pick a node from the hierarchy to review metadata, manage children, move it, or work with its OEE machine link.",
  },
  detailsIntro: {
    id: `${scope}.details.intro`,
    defaultMessage: "Selected node details and available maintenance actions.",
  },
  statusActive: { id: `${scope}.details.status.active`, defaultMessage: "Active" },
  statusInactive: { id: `${scope}.details.status.inactive`, defaultMessage: "Inactive" },
  machineCardTitle: { id: `${scope}.details.machineCard`, defaultMessage: "Machine card" },
  actions: { id: `${scope}.details.actions`, defaultMessage: "Actions" },
  fieldName: { id: `${scope}.field.name`, defaultMessage: "Name" },
  fieldType: { id: `${scope}.field.type`, defaultMessage: "Type" },
  fieldParent: { id: `${scope}.field.parent`, defaultMessage: "Parent" },
  fieldSortOrder: { id: `${scope}.field.sortOrder`, defaultMessage: "Sort order" },
  fieldDescription: { id: `${scope}.field.description`, defaultMessage: "Description" },
  fieldTitle: { id: `${scope}.field.title`, defaultMessage: "Title" },
  fieldManufacturer: { id: `${scope}.field.manufacturer`, defaultMessage: "Manufacturer" },
  fieldModel: { id: `${scope}.field.model`, defaultMessage: "Model" },
  fieldModelYear: { id: `${scope}.field.modelYear`, defaultMessage: "Model year" },
  fieldSerialNumber: { id: `${scope}.field.serialNumber`, defaultMessage: "Serial number" },
  valueNone: { id: `${scope}.value.none`, defaultMessage: "None" },
  addChild: { id: `${scope}.action.addChild`, defaultMessage: "Add child" },
  move: { id: `${scope}.action.move`, defaultMessage: "Move" },
  deactivate: { id: `${scope}.action.deactivate`, defaultMessage: "Deactivate" },
  reportProblem: { id: `${scope}.action.reportProblem`, defaultMessage: "Report problem" },
  tabDocuments: { id: `${scope}.tab.documents`, defaultMessage: "Documents" },
  tabMachine: { id: `${scope}.tab.machine`, defaultMessage: "Machine" },
  tabUsage: { id: `${scope}.tab.usage`, defaultMessage: "Usage" },
  tabHistory: { id: `${scope}.tab.history`, defaultMessage: "History" },
  tabDescription: { id: `${scope}.tab.description`, defaultMessage: "Description" },
  descriptionEmpty: { id: `${scope}.description.empty`, defaultMessage: "No description for this asset yet." },
  descriptionAdd: { id: `${scope}.description.add`, defaultMessage: "Add description" },
  toastDescriptionSaved: { id: `${scope}.toast.descriptionSaved`, defaultMessage: "Description saved" },

  // history tab. The other column headers reuse the work order page's field.* messages.
  historyColumnFinished: { id: `${scope}.history.column.finished`, defaultMessage: "Finished" },
  historyEmptyTitle: { id: `${scope}.history.emptyTitle`, defaultMessage: "No completed work orders" },
  historyEmptyHint: {
    id: `${scope}.history.emptyHint`,
    defaultMessage: "Completed and closed work orders on this node and everything below it are listed here.",
  },
  historyLoadFailed: { id: `${scope}.history.loadFailed`, defaultMessage: "Could not load the work order history" },

  // planned maintenance tab. The overdue marker reuses usage.scheduleOverdue and the next run
  // tooltip the schedules page's column header.
  tabMaintenance: { id: `${scope}.tab.maintenance`, defaultMessage: "Planned maintenance" },
  maintenanceWorkOrdersTitle: { id: `${scope}.maintenance.workOrdersTitle`, defaultMessage: "Active work orders" },
  maintenanceWorkOrdersEmptyTitle: {
    id: `${scope}.maintenance.workOrdersEmptyTitle`,
    defaultMessage: "No active preventive work orders",
  },
  maintenanceWorkOrdersEmptyHint: {
    id: `${scope}.maintenance.workOrdersEmptyHint`,
    defaultMessage: "Preventive work orders on this node and everything below it that are not yet completed or closed are listed here.",
  },
  maintenanceWorkOrdersLoadFailed: {
    id: `${scope}.maintenance.workOrdersLoadFailed`,
    defaultMessage: "Could not load the active work orders",
  },
  maintenancePlannedStart: { id: `${scope}.maintenance.plannedStart`, defaultMessage: "Planned {date}" },
  maintenanceSchedulesTitle: { id: `${scope}.maintenance.schedulesTitle`, defaultMessage: "Upcoming schedules" },
  maintenanceSchedulesEmptyTitle: { id: `${scope}.maintenance.schedulesEmptyTitle`, defaultMessage: "No active schedules" },
  maintenanceSchedulesEmptyHint: {
    id: `${scope}.maintenance.schedulesEmptyHint`,
    defaultMessage: "Active recurring schedules on this node and everything below it are listed here.",
  },
  maintenanceSchedulesLoadFailed: {
    id: `${scope}.maintenance.schedulesLoadFailed`,
    defaultMessage: "Could not load the upcoming schedules",
  },
  maintenanceNotMeasured: { id: `${scope}.maintenance.notMeasured`, defaultMessage: "No reading" },

  // usage tab
  usageRunTime: { id: `${scope}.usage.runTime`, defaultMessage: "Run time" },
  usageCycles: { id: `${scope}.usage.cycles`, defaultMessage: "Cycles" },
  usageMachineCount: {
    id: `${scope}.usage.machineCount`,
    defaultMessage: "{count} {count, plural, one {machine} other {machines}}",
  },
  usageNoMachines: { id: `${scope}.usage.noMachines`, defaultMessage: "No machines connected to this node" },
  usageNoMachinesHint: {
    id: `${scope}.usage.noMachinesHint`,
    defaultMessage: "Link an OEE machine in the Machine tab to see consumption data.",
  },
  usageScheduleOverdue: { id: `${scope}.usage.scheduleOverdue`, defaultMessage: "Overdue" },
  usageScheduleRunningTime: { id: `${scope}.usage.scheduleRunningTime`, defaultMessage: "Running time" },
  usageScheduleQuantity: { id: `${scope}.usage.scheduleQuantity`, defaultMessage: "Cycles" },

  // node form dialog
  addNodeDialogTitle: { id: `${scope}.nodeForm.addTitle`, defaultMessage: "Add asset node" },
  editNodeDialogTitle: { id: `${scope}.nodeForm.editTitle`, defaultMessage: "Edit asset node" },
  editNodeIntro: {
    id: `${scope}.nodeForm.editIntro`,
    defaultMessage: "Update metadata for the selected asset node. Status changes still follow the CMMS activation rules.",
  },
  addNodeIntro: {
    id: `${scope}.nodeForm.addIntro`,
    defaultMessage: "Create a child node under the selected parent and optionally link it to one OEE machine if it is a maintenance object.",
  },
  addRootDialogTitle: { id: `${scope}.nodeForm.addRootTitle`, defaultMessage: "Create site" },
  addRootIntro: {
    id: `${scope}.nodeForm.addRootIntro`,
    defaultMessage: "A site is the top of the hierarchy and has no parent. Departments and maintenance objects are added underneath it afterwards.",
  },
  customObject: { id: `${scope}.nodeForm.customObject`, defaultMessage: "Custom object" },
  fieldActive: { id: `${scope}.field.active`, defaultMessage: "Active" },

  // machine assignment
  machineLinkTitle: { id: `${scope}.machineLink.title`, defaultMessage: "OEE machine link" },
  machineLinkIntroObject: {
    id: `${scope}.machineLink.introObject`,
    defaultMessage: "Each maintenance object can link to one OEE machine. Custom maintenance objects can stay unlinked.",
  },
  machineLinkIntroOther: {
    id: `${scope}.machineLink.introOther`,
    defaultMessage: "Only maintenance objects can link to an OEE machine.",
  },
  machineLinkSelectObject: {
    id: `${scope}.machineLink.selectObject`,
    defaultMessage: "Select a maintenance object to manage the OEE machine link.",
  },
  machineLinkLinkedFallback: { id: `${scope}.machineLink.linkedFallback`, defaultMessage: "Linked machine" },
  machineLinkLinkedChip: { id: `${scope}.machineLink.linkedChip`, defaultMessage: "Linked" },
  machineLinkAlreadyMapped: {
    id: `${scope}.machineLink.alreadyMapped`,
    defaultMessage: "This maintenance object is already mapped to an OEE machine.",
  },
  machineLinkUnlink: { id: `${scope}.machineLink.unlink`, defaultMessage: "Unlink" },
  machineLinkLink: { id: `${scope}.machineLink.link`, defaultMessage: "Link" },
  machineLinkField: { id: `${scope}.machineLink.field`, defaultMessage: "OEE machine" },
  machineLinkActivateFirst: {
    id: `${scope}.machineLink.activateFirst`,
    defaultMessage: "Activate the node before linking it to an OEE machine.",
  },
  machineLinkNoneAvailable: {
    id: `${scope}.machineLink.noneAvailable`,
    defaultMessage: "No unlinked OEE machines are available right now.",
  },
  machineLinkLeaveEmpty: {
    id: `${scope}.machineLink.leaveEmpty`,
    defaultMessage: "Leave the selection empty if this should stay a custom maintenance object.",
  },

  // the tool mounted in the linked machine
  // "Could not read" is its own answer and never folded into "no tool mounted": one of the two
  // is a fact about the machine, the other is a fact about us.
  machineToolFailed: { id: `${scope}.machineTool.failed`, defaultMessage: "Could not read the mounted tool" },
  machineToolHeading: { id: `${scope}.machineTool.heading`, defaultMessage: "Mounted tool" },
  machineToolNone: { id: `${scope}.machineTool.none`, defaultMessage: "No tool mounted" },
  // Says "no match" rather than "not in the register": an ambiguous name resolves the same way
  // as a missing one, and only one of those two is the register's fault.
  machineToolUnmatched: {
    id: `${scope}.machineTool.unmatched`,
    defaultMessage: "No match for {tool} in the tool register",
  },

  // reparent dialog
  moveDialogTitle: { id: `${scope}.reparent.title`, defaultMessage: "Move asset node" },
  moveDialogIntro: {
    id: `${scope}.reparent.intro`,
    defaultMessage: "Move {name} to a new parent that matches the allowed CMMS hierarchy rules.",
  },
  moveDialogThisNode: { id: `${scope}.reparent.thisNode`, defaultMessage: "this node" },
  moveDialogNewParent: { id: `${scope}.reparent.newParent`, defaultMessage: "New parent" },
  moveDialogNoTargets: { id: `${scope}.reparent.noTargets`, defaultMessage: "No valid parent nodes" },

  // quick report dialog
  reportDialogTitle: { id: `${scope}.report.title`, defaultMessage: "Report a problem" },
  reportDefaultTitle: { id: `${scope}.report.defaultTitle`, defaultMessage: "Problem with {asset}" },
  reportAssetLabel: { id: `${scope}.report.assetLabel`, defaultMessage: "Asset" },
  reportTypeStoppage: { id: `${scope}.report.type.stoppage`, defaultMessage: "Stoppage" },
  reportTypeProblem: { id: `${scope}.report.type.problem`, defaultMessage: "Problem" },
  // Whole sentences with the tool as a parameter rather than a label concatenated with a
  // value: "Label: " + value breaks word order in other languages.
  reportActiveTool: { id: `${scope}.report.activeTool`, defaultMessage: "Tool in the machine: {tool}" },
  reportActiveToolUnknown: { id: `${scope}.report.activeToolUnknown`, defaultMessage: "Tool in the machine: {tool} (not in the tool register)" },
  reportAddPhoto: { id: `${scope}.report.addPhoto`, defaultMessage: "Add photo" },
  reportRemovePhoto: { id: `${scope}.report.removePhoto`, defaultMessage: "Remove" },
  reportSubmit: { id: `${scope}.report.submit`, defaultMessage: "Report" },
  reportTitleRequired: { id: `${scope}.report.titleRequired`, defaultMessage: "Title is required" },
  reportNoAsset: { id: `${scope}.report.noAsset`, defaultMessage: "No asset selected" },
  reportFailed: { id: `${scope}.report.failed`, defaultMessage: "Could not report the issue" },
  reportPhotoFailed: {
    id: `${scope}.report.photoFailed`,
    defaultMessage: "Issue reported, but the photo failed to upload: {error}",
  },
  reportSuccess: { id: `${scope}.report.success`, defaultMessage: "Problem reported" },

  // documents — list
  documentsCount: { id: `${scope}.documents.count`, defaultMessage: "Documents ({count})" },
  documentsEmpty: { id: `${scope}.documents.empty`, defaultMessage: "No documents for this asset yet." },
  documentsUploadButton: { id: `${scope}.documents.uploadButton`, defaultMessage: "Upload document" },
  documentsColumnSize: { id: `${scope}.documents.column.size`, defaultMessage: "Size" },
  documentsColumnUploadedBy: { id: `${scope}.documents.column.uploadedBy`, defaultMessage: "Uploaded by" },
  documentsColumnUploadedAt: { id: `${scope}.documents.column.uploadedAt`, defaultMessage: "Uploaded at" },
  documentsEditAria: { id: `${scope}.documents.editAria`, defaultMessage: "Edit document" },
  documentsDeleteAria: { id: `${scope}.documents.deleteAria`, defaultMessage: "Delete document" },
  documentsDeleteTitle: { id: `${scope}.documents.deleteTitle`, defaultMessage: "Delete document" },
  documentsDeleteMessage: {
    id: `${scope}.documents.deleteMessage`,
    defaultMessage: "Delete \"{title}\"? The stored file is removed as well.",
  },
  documentsDeleteOverviewImageMessage: {
    id: `${scope}.documents.deleteOverviewImageMessage`,
    defaultMessage: "Delete \"{title}\"? It is the machine card's overview image, so the card is left without one. The stored file is removed as well.",
  },
  documentsDownloadTitle: { id: `${scope}.documents.downloadTitle`, defaultMessage: "Download {fileName}" },
  documentsDeleted: { id: `${scope}.documents.deleted`, defaultMessage: "Document deleted" },
  documentsOperationFailed: { id: `${scope}.documents.operationFailed`, defaultMessage: "Operation failed" },

  // documents — upload dialog
  uploadDialogTitle: { id: `${scope}.upload.title`, defaultMessage: "Upload document" },
  uploadDialogIntro: {
    id: `${scope}.upload.intro`,
    defaultMessage: "Attach a manual, drawing, certificate or photo to this asset. Maximum file size is {max}.",
  },
  uploadDropHint: { id: `${scope}.upload.dropHint`, defaultMessage: "Drag a file here, or choose one below." },
  uploadChooseFile: { id: `${scope}.upload.chooseFile`, defaultMessage: "Choose file" },
  uploadChooseAnother: { id: `${scope}.upload.chooseAnother`, defaultMessage: "Choose another file" },
  uploadTakePhoto: { id: `${scope}.upload.takePhoto`, defaultMessage: "Take photo" },
  uploadTooLarge: {
    id: `${scope}.upload.tooLarge`,
    defaultMessage: "{name} is {size}. The maximum upload size is {max}.",
  },
  uploadTypeNotAllowed: {
    id: `${scope}.upload.typeNotAllowed`,
    defaultMessage: "{name} is a {type} file, which is not an accepted document type.",
  },
  uploadTypeUnknown: { id: `${scope}.upload.typeUnknown`, defaultMessage: "unknown" },
  uploadProgress: { id: `${scope}.upload.progress`, defaultMessage: "Uploading… {percent}%" },
  uploadSubmit: { id: `${scope}.upload.submit`, defaultMessage: "Upload" },
  uploadSuccess: { id: `${scope}.upload.success`, defaultMessage: "Document uploaded" },
  uploadFailed: { id: `${scope}.upload.failed`, defaultMessage: "Upload failed" },

  // documents — edit dialog
  editDocumentTitle: { id: `${scope}.editDocument.title`, defaultMessage: "Edit document" },
  editDocumentIntro: {
    id: `${scope}.editDocument.intro`,
    defaultMessage: "Update the title, type and description. The uploaded file itself is not replaced — upload a new document if the file has changed.",
  },
  editDocumentFileLine: { id: `${scope}.editDocument.fileLine`, defaultMessage: "File: {fileName}" },
  editDocumentUpdated: { id: `${scope}.editDocument.updated`, defaultMessage: "Document updated" },
  editDocumentFailed: { id: `${scope}.editDocument.failed`, defaultMessage: "Update failed" },

  // machine card — overview image
  overviewImageAlt: { id: `${scope}.overviewImage.alt`, defaultMessage: "Overview image of {name}" },
  overviewImageEmpty: { id: `${scope}.overviewImage.empty`, defaultMessage: "No overview image" },
  overviewImageLoadFailed: { id: `${scope}.overviewImage.loadFailed`, defaultMessage: "The overview image could not be loaded" },
  overviewImageUpload: { id: `${scope}.overviewImage.upload`, defaultMessage: "Upload" },
  overviewImageChoose: { id: `${scope}.overviewImage.choose`, defaultMessage: "Choose existing" },
  overviewImageRemove: { id: `${scope}.overviewImage.remove`, defaultMessage: "Remove" },
  overviewImageChip: { id: `${scope}.overviewImage.chip`, defaultMessage: "Overview image" },
  overviewImageSet: { id: `${scope}.overviewImage.set`, defaultMessage: "Overview image updated" },
  overviewImageRemoved: {
    id: `${scope}.overviewImage.removed`,
    defaultMessage: "Overview image removed. The document is still in the list.",
  },
  overviewImageFailed: { id: `${scope}.overviewImage.failed`, defaultMessage: "Could not update the overview image" },
  overviewImageUploadedNotSet: {
    id: `${scope}.overviewImage.uploadedNotSet`,
    defaultMessage: "The photo was uploaded, but could not be set as the overview image: {error}",
  },
  overviewImagePickerTitle: { id: `${scope}.overviewImage.pickerTitle`, defaultMessage: "Choose overview image" },
  overviewImagePickerIntro: {
    id: `${scope}.overviewImage.pickerIntro`,
    defaultMessage: "Pick one of this asset's images. JPEG, PNG, GIF and WebP images can be shown on the card.",
  },
  overviewImagePickerEmpty: {
    id: `${scope}.overviewImage.pickerEmpty`,
    defaultMessage: "This asset has no images that can be shown on the card. Upload one instead.",
  },

  // index toasts + confirmations
  toastNameRequired: { id: `${scope}.toast.nameRequired`, defaultMessage: "Name is required" },
  toastModelYearInvalid: {
    id: `${scope}.toast.modelYearInvalid`,
    defaultMessage: "Model year must be a year between {minYear} and {maxYear}",
  },
  toastNodeSaved: { id: `${scope}.toast.nodeSaved`, defaultMessage: "Asset node saved" },
  toastNodeCreated: { id: `${scope}.toast.nodeCreated`, defaultMessage: "Asset node created" },
  toastNodeMoved: { id: `${scope}.toast.nodeMoved`, defaultMessage: "Asset node moved" },
  toastNodeDeactivated: { id: `${scope}.toast.nodeDeactivated`, defaultMessage: "Asset node deactivated" },
  toastMachineLinked: { id: `${scope}.toast.machineLinked`, defaultMessage: "OEE machine linked" },
  toastMachineUnlinked: { id: `${scope}.toast.machineUnlinked`, defaultMessage: "OEE machine unlinked" },
  confirmDeactivateTitle: { id: `${scope}.confirm.deactivateTitle`, defaultMessage: "Deactivate asset node" },
  confirmDeactivateMessage: { id: `${scope}.confirm.deactivateMessage`, defaultMessage: "Deactivate {name}?" },
  confirmUnlinkTitle: { id: `${scope}.confirm.unlinkTitle`, defaultMessage: "Unlink machine" },
  confirmUnlinkMessage: { id: `${scope}.confirm.unlinkMessage`, defaultMessage: "Unlink {name}?" },
});
